import {
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	FormControlLabel,
	Grid,
	Snackbar,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import React, { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { useAssetContext } from "../../../../../context/assetContext";
import { TreePickerControl } from "../../../../../controls";
import { useConfigurationAssetContext } from "../../context/ConfigurationAssetContext";
import { AssetAPI } from "../../models/AssetAPI";
import { DATA_ASSET_INITIAL_VALUES } from "../../models/InitialValueDataAsset";
import { insertAsset } from "../../repositories/ConfigurationAssetRepository";
import { useAssetCodes } from "../hooks/useAssetCodes";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiPaper-root": {
			height: "auto",
		},
	},
	checkbox: {
		userSelect: "none",
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalTitleCloseWO: {
		backgroundColor: "#28a745",
		color: "white",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 120,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateNewAssetDialog = ({ show, onHide }: Props) => {
	const classes = useStyles();
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [assetCreatedSuccess, setAssetCreatedSuccess] =
		useState<boolean>(false);

	const [errorCreateAsset, setErrorCreateAsset] = useState<string>("");
	const [isCreateSubmitLoading, setIsCreateSubmitLoading] = useState(false);

	const {
		state: { allAssets },
	} = useAssetContext();

	const {
		state: {
			assetNodeSelectedInTree,
			assetCodes,
			dataToCreateAsset,
			assetTreeName,
		},
		actions: {
			setAssetNameToCreateAsset,
			setAssetTreeName,
			setAssetParentAssetIdToCreateAsset,
			setParentAssetNameInNodeSelectedInTree,
		},
	} = useConfigurationAssetContext();

	const { loadFilterAssets } = useAssetCodes();

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<AssetAPI>({
		defaultValues: dataToCreateAsset
			? dataToCreateAsset
			: DATA_ASSET_INITIAL_VALUES,
	});

	const handleCancel = () => {
		onHide(false);
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetCreatedSuccess(false);
	};

	const handleCreateAsset = async (data: AssetAPI) => {
		setIsCreateSubmitLoading(true);
		const res = await insertAsset(
			data.AssetName,
			data.ParentAssetId as number,
			data.CanBeDefaultAsset,
		);
		if (res.ok) {
			setIsCreateSubmitLoading(false);
			setAssetCreatedSuccess(true);
			setValue("AssetName", "");
			onHide(true);
		} else {
			setIsCreateSubmitLoading(false);
			setErrorCreateAsset(res.message);
		}
	};

	const changeParentAssetId = (value: any, description: any) => {
		setValue("ParentAssetId", Number.parseInt(value));
		setAssetParentAssetIdToCreateAsset(Number.parseInt(value));
		setParentAssetNameInNodeSelectedInTree(description);
	};

	const handleFormSubmit = handleSubmit(handleCreateAsset);

	useEffect(() => {
		if (show) {
			reset();
			loadFilterAssets();
			if (assetNodeSelectedInTree?.AssetId) {
				setValue(
					"ParentAssetId",
					Number.parseInt(assetNodeSelectedInTree?.AssetId as string),
				);
				const parentAssetNode = allAssets.find(
					(node: any) => node.AssetId === assetNodeSelectedInTree?.AssetId,
				);
				setParentAssetNameInNodeSelectedInTree(parentAssetNode?.AssetName);
			} else {
				setAssetParentAssetIdToCreateAsset(1);
				setAssetTreeName("Plant");
				setAssetNameToCreateAsset("");
			}
		}
	}, [show]);

	return (
		<div className={classes.root}>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
				disableEnforceFocus
				className={classes.root}
			>
				<form onSubmit={handleFormSubmit}>
					<Grid container style={{ padding: "15px 0 20px", height: "50%" }}>
						<Grid container item>
							<Grid item md={12} xs={12}>
								<Typography style={{ padding: "0 24px 12px", fontSize: 25 }}>
									NEW ASSET
								</Typography>
							</Grid>
						</Grid>
						<Grid container style={{ alignItems: "center" }}>
							<DialogContent dividers style={{ padding: "10px 24px 0" }}>
								<Grid item md={12} xs={12}>
									<Controller
										name="AssetName"
										control={control}
										rules={{
											validate: {
												notEmpty: (value) => value.trim() !== "",
											},
										}}
										render={({ field: { onChange, value } }) => (
											<>
												<TextField
													label="Asset Name"
													variant="outlined"
													error={!!errors.AssetName}
													fullWidth
													margin="dense"
													autoComplete="off"
													onChange={onChange}
													value={value}
												/>
												{errors.AssetName?.type === "notEmpty" && (
													<span className={classes.errorLabel}>
														Asset Name is required
													</span>
												)}
											</>
										)}
									/>
								</Grid>
								<Grid container>
									<Grid item md={12} xs={12} style={{ marginBottom: "8px" }}>
										<Grid
											item
											md={12}
											xs={12}
											style={{ padding: 0, marginTop: 10 }}
										>
											<Controller
												name="ParentAssetId"
												control={control}
												render={({}) => (
													<TreePickerControl
														inputTitle="Asset"
														title="Asset Parent"
														dataSource={assetCodes}
														value={assetNodeSelectedInTree?.AssetId || 1}
														description={
															(assetNodeSelectedInTree?.ParentAssetName as string) ||
															"Plant"
														}
														onSelect={changeParentAssetId}
														selectBranch={true}
														selectActive={false}
														selectInternal={false}
													></TreePickerControl>
												)}
											/>
										</Grid>
									</Grid>
									<Grid container style={{ marginTop: "10px 0", padding: "0" }}>
										<Grid item xs={6} md={6}>
											<Controller
												name="CanBeDefaultAsset"
												control={control}
												defaultValue={false}
												render={({ field }) => (
													<FormControlLabel
														className={classes.checkbox}
														control={
															<Checkbox
																checked={field.value as boolean}
																onChange={(e) =>
																	field.onChange(e.target.checked)
																}
																name="CanBeDefaultAsset"
																color="primary"
															/>
														}
														label="Can be Default Asset"
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>
							</DialogContent>
							<Grid item md={12} xs={12} style={{ marginTop: "15px" }}>
								<DialogActions style={{ padding: "0px 24px" }}>
									<Grid
										container
										justifyContent="flex-end"
										style={{ margin: 0 }}
									>
										<Grid
											container
											item
											style={{
												marginLeft: "auto",
												marginTop: 5,
												marginBottom: 0,
											}}
										>
											<Grid
												item
												md={6}
												xs={12}
												style={{ paddingLeft: "0", paddingRight: "16px" }}
											>
												<Button
													fullWidth
													variant="contained"
													color="default"
													onClick={() => handleCancel()}
												>
													Cancel
												</Button>
											</Grid>
											<Grid item md={6} xs={12}>
												<Button
													fullWidth
													startIcon={
														isCreateSubmitLoading && (
															<CircularProgress size="1rem" />
														)
													}
													disabled={isCreateSubmitLoading}
													variant="contained"
													color="primary"
													type="submit"
												>
													Save
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</DialogActions>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Dialog>
			<ErrorModal
				error={errorCreateAsset}
				onHide={() => setErrorCreateAsset("")}
			/>
			<Snackbar
				open={assetCreatedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Document created succesfully
				</Alert>
			</Snackbar>
		</div>
	);
};
